// MetadataSection.js
// Modified and Reorganized MetadataSection.js with FeatureGuard
import React, { useState, useEffect } from 'react';
import { Loader, Check, AlertCircle, MapPin, RefreshCw } from 'lucide-react';

import SimplifiedMapWithSearch from '../../maps/SimplifiedMapWithSearch';
import ImageUploader from './../ui/ImageUploader';
import KnowledgeDocPanel from './KnowledgeDocPanel';
import generateTagsAndCategories from './../utils/generateTagsAndCategories';
import { validateFilename, generateSafeGuideFilename } from './filenameSanitizer';
import LanguageSelector, { LANGUAGES } from '../../selectors/LanguageSelector';
import ArchetypeClassifier from './ArchetypeClassifier';
import { buildKnowledgeContext } from './knowledgeDocumentUtils';
import { parseBatchSupplementaryContentResponse } from './supplementaryContentParser';
import { normalizeMapDisplaySettings } from './utils';
import CollapsibleSection from '../../ui/CollapsibleSection';

// Access control imports
import { useAccessControl } from '../../../contexts/AccessControlContext';
import FeatureGuard from '../../FeatureGuard';
import { aiService } from '../../services/aiService';
import { AI_PROVIDERS, getProviderForModel, API_KEYS } from '../../constants/aiConstants';

const cloneFeedbackValue = (value) => JSON.parse(JSON.stringify(value));
const FEEDBACK_TEMPLATE_LANGUAGES = ['en', 'es', 'pt', 'fr'];
const LOCKED_TRANSLATED_METADATA_FIELDS = new Set([
  'country',
  'city',
  'data_source',
  'theme',
  'itinerary_duration',
  'itinerary_difficulty',
  'thumbnailUrl',
  'itinerary_coordinates',
  'guide_type',
  'itinerary_categories',
  'itinerary_tags',
  'isIntroductoryStop',
  'download_on_location',
  'download_radius_meters',
  'download_priority',
  'auto_play_radius_meters',
  'ai_radius_limitation',
  'ai_radius_meters',
  '360visit',
  '360visitJSON',
  '360visitUrl',
  'web_based_guide',
  'has_qr_codes',
  'orientation',
  'map_display',
  'b2bClient',
  'feedbackCollect',
  'travel_dna_archetype'
]);

const DEFAULT_FEEDBACK_CONFIGS = {
  en: {
  micro_prompts: [
    {
      id: 'visit_impact',
      kind: 'visit_impact',
      title: 'Quick feedback',
      prompt: 'Did this guide improve your visit?',
      feedbackType: 'micro_prompt',
      options: [
        {
          id: 'yes_a_lot',
          label: 'Yes, a lot',
          value: 'yes_a_lot',
          rating: 5,
          advances_to_next: true,
        },
        {
          id: 'yes_somewhat',
          label: 'Yes, somewhat',
          value: 'yes_somewhat',
          rating: 4,
          advances_to_next: true,
        },
        {
          id: 'not_really',
          label: 'Not really',
          value: 'not_really',
          rating: 2,
          advances_to_next: false,
        },
      ],
    },
    {
      id: 'learning_value',
      kind: 'learning_value',
      title: 'One more question',
      prompt: 'Did you learn something new?',
      feedbackType: 'micro_prompt',
      options: [
        {
          id: 'yes',
          label: 'Yes',
          value: 'yes',
          rating: 5,
          advances_to_next: true,
        },
        {
          id: 'a_little',
          label: 'A little',
          value: 'a_little',
          rating: 3,
          advances_to_next: false,
        },
        {
          id: 'no',
          label: 'No',
          value: 'no',
          rating: 1,
          advances_to_next: false,
        },
      ],
    },
    {
      id: 'recommendation',
      kind: 'recommendation',
      title: 'Last one',
      prompt: 'Would you recommend this guide to other visitors?',
      feedbackType: 'micro_prompt',
      options: [
        {
          id: 'definitely',
          label: 'Definitely',
          value: 'definitely',
          rating: 5,
          advances_to_next: true,
        },
        {
          id: 'maybe',
          label: 'Maybe',
          value: 'maybe',
          rating: 4,
          advances_to_next: true,
        },
        {
          id: 'not_yet',
          label: 'Not yet',
          value: 'not_yet',
          rating: 2,
          advances_to_next: false,
        },
      ],
    },
  ],
  issue_question: {
    id: 'main_issue',
    kind: 'issue_picker',
    title: 'Thanks. What were the main issues?',
    prompt: 'Choose any problems that apply so we can improve the guide.',
    feedbackType: 'issue_recovery',
    options: [
      { id: 'audio_quality', label: 'Audio quality', value: 'audio_quality' },
      { id: 'too_much_information', label: 'Too much information', value: 'too_much_information' },
      { id: 'not_enough_information', label: 'Not enough information', value: 'not_enough_information' },
      { id: 'hard_to_follow', label: 'Hard to follow', value: 'hard_to_follow' },
      { id: 'bad_timing', label: 'Bad timing', value: 'bad_timing' },
      { id: 'navigation_confusion', label: 'Navigation confusion', value: 'navigation_confusion' },
      { id: 'language_issue', label: 'Language issue', value: 'language_issue' },
      { id: 'content_not_relevant', label: 'Content not relevant', value: 'content_not_relevant' },
      { id: 'technical_issue', label: 'Technical issue', value: 'technical_issue' },
    ],
  },
  },
  es: {
    micro_prompts: [
      {
        id: 'visit_impact',
        kind: 'visit_impact',
        title: 'Feedback rápido',
        prompt: '¿Este guía mejoró tu visita?',
        feedbackType: 'micro_prompt',
        options: [
          { id: 'yes_a_lot', label: 'Sí, mucho', value: 'yes_a_lot', rating: 5, advances_to_next: true },
          { id: 'yes_somewhat', label: 'Sí, algo', value: 'yes_somewhat', rating: 4, advances_to_next: true },
          { id: 'not_really', label: 'No realmente', value: 'not_really', rating: 2, advances_to_next: false },
        ],
      },
      {
        id: 'learning_value',
        kind: 'learning_value',
        title: 'Una pregunta más',
        prompt: '¿Aprendiste algo nuevo?',
        feedbackType: 'micro_prompt',
        options: [
          { id: 'yes', label: 'Sí', value: 'yes', rating: 5, advances_to_next: true },
          { id: 'a_little', label: 'Un poco', value: 'a_little', rating: 3, advances_to_next: false },
          { id: 'no', label: 'No', value: 'no', rating: 1, advances_to_next: false },
        ],
      },
      {
        id: 'recommendation',
        kind: 'recommendation',
        title: 'La última',
        prompt: '¿Recomendarías este guía a otros visitantes?',
        feedbackType: 'micro_prompt',
        options: [
          { id: 'definitely', label: 'Definitivamente', value: 'definitely', rating: 5, advances_to_next: true },
          { id: 'maybe', label: 'Tal vez', value: 'maybe', rating: 4, advances_to_next: true },
          { id: 'not_yet', label: 'Todavía no', value: 'not_yet', rating: 2, advances_to_next: false },
        ],
      },
    ],
    issue_question: {
      id: 'main_issue',
      kind: 'issue_picker',
      title: 'Gracias. ¿Cuáles fueron los principales problemas?',
      prompt: 'Selecciona los problemas que apliquen para ayudarnos a mejorar el guía.',
      feedbackType: 'issue_recovery',
      options: [
        { id: 'audio_quality', label: 'Calidad de audio', value: 'audio_quality' },
        { id: 'too_much_information', label: 'Demasiada información', value: 'too_much_information' },
        { id: 'not_enough_information', label: 'Poca información', value: 'not_enough_information' },
        { id: 'hard_to_follow', label: 'Difícil de seguir', value: 'hard_to_follow' },
        { id: 'bad_timing', label: 'Mal sincronizado', value: 'bad_timing' },
        { id: 'navigation_confusion', label: 'Confusión de navegación', value: 'navigation_confusion' },
        { id: 'language_issue', label: 'Problema de idioma', value: 'language_issue' },
        { id: 'content_not_relevant', label: 'Contenido no relevante', value: 'content_not_relevant' },
        { id: 'technical_issue', label: 'Problema técnico', value: 'technical_issue' },
      ],
    },
  },
  pt: {
    micro_prompts: [
      {
        id: 'visit_impact',
        kind: 'visit_impact',
        title: 'Feedback rápido',
        prompt: 'Este guia melhorou a tua visita?',
        feedbackType: 'micro_prompt',
        options: [
          { id: 'yes_a_lot', label: 'Sim, muito', value: 'yes_a_lot', rating: 5, advances_to_next: true },
          { id: 'yes_somewhat', label: 'Sim, um pouco', value: 'yes_somewhat', rating: 4, advances_to_next: true },
          { id: 'not_really', label: 'Não muito', value: 'not_really', rating: 2, advances_to_next: false },
        ],
      },
      {
        id: 'learning_value',
        kind: 'learning_value',
        title: 'Mais uma pergunta',
        prompt: 'Aprendeste algo novo?',
        feedbackType: 'micro_prompt',
        options: [
          { id: 'yes', label: 'Sim', value: 'yes', rating: 5, advances_to_next: true },
          { id: 'a_little', label: 'Um pouco', value: 'a_little', rating: 3, advances_to_next: false },
          { id: 'no', label: 'Não', value: 'no', rating: 1, advances_to_next: false },
        ],
      },
      {
        id: 'recommendation',
        kind: 'recommendation',
        title: 'A última',
        prompt: 'Recomendarias este guia a outros visitantes?',
        feedbackType: 'micro_prompt',
        options: [
          { id: 'definitely', label: 'Certamente', value: 'definitely', rating: 5, advances_to_next: true },
          { id: 'maybe', label: 'Talvez', value: 'maybe', rating: 4, advances_to_next: true },
          { id: 'not_yet', label: 'Ainda não', value: 'not_yet', rating: 2, advances_to_next: false },
        ],
      },
    ],
    issue_question: {
      id: 'main_issue',
      kind: 'issue_picker',
      title: 'Obrigado. Quais foram os principais problemas?',
      prompt: 'Seleciona os problemas que se aplicam para nos ajudar a melhorar o guia.',
      feedbackType: 'issue_recovery',
      options: [
        { id: 'audio_quality', label: 'Qualidade de áudio', value: 'audio_quality' },
        { id: 'too_much_information', label: 'Demasiada informação', value: 'too_much_information' },
        { id: 'not_enough_information', label: 'Pouca informação', value: 'not_enough_information' },
        { id: 'hard_to_follow', label: 'Difícil de seguir', value: 'hard_to_follow' },
        { id: 'bad_timing', label: 'Mau timing', value: 'bad_timing' },
        { id: 'navigation_confusion', label: 'Confusão de navegação', value: 'navigation_confusion' },
        { id: 'language_issue', label: 'Problema de idioma', value: 'language_issue' },
        { id: 'content_not_relevant', label: 'Conteúdo não relevante', value: 'content_not_relevant' },
        { id: 'technical_issue', label: 'Problema técnico', value: 'technical_issue' },
      ],
    },
  },
  fr: {
    micro_prompts: [
      {
        id: 'visit_impact',
        kind: 'visit_impact',
        title: 'Retour rapide',
        prompt: 'Ce guide a-t-il amélioré votre visite ?',
        feedbackType: 'micro_prompt',
        options: [
          { id: 'yes_a_lot', label: 'Oui, beaucoup', value: 'yes_a_lot', rating: 5, advances_to_next: true },
          { id: 'yes_somewhat', label: 'Oui, un peu', value: 'yes_somewhat', rating: 4, advances_to_next: true },
          { id: 'not_really', label: 'Pas vraiment', value: 'not_really', rating: 2, advances_to_next: false },
        ],
      },
      {
        id: 'learning_value',
        kind: 'learning_value',
        title: 'Une autre question',
        prompt: 'Avez-vous appris quelque chose de nouveau ?',
        feedbackType: 'micro_prompt',
        options: [
          { id: 'yes', label: 'Oui', value: 'yes', rating: 5, advances_to_next: true },
          { id: 'a_little', label: 'Un peu', value: 'a_little', rating: 3, advances_to_next: false },
          { id: 'no', label: 'Non', value: 'no', rating: 1, advances_to_next: false },
        ],
      },
      {
        id: 'recommendation',
        kind: 'recommendation',
        title: 'La dernière',
        prompt: 'Recommanderiez-vous ce guide à d’autres visiteurs ?',
        feedbackType: 'micro_prompt',
        options: [
          { id: 'definitely', label: 'Certainement', value: 'definitely', rating: 5, advances_to_next: true },
          { id: 'maybe', label: 'Peut-être', value: 'maybe', rating: 4, advances_to_next: true },
          { id: 'not_yet', label: 'Pas encore', value: 'not_yet', rating: 2, advances_to_next: false },
        ],
      },
    ],
    issue_question: {
      id: 'main_issue',
      kind: 'issue_picker',
      title: 'Merci. Quels étaient les principaux problèmes ?',
      prompt: 'Sélectionnez les problèmes qui s’appliquent afin de nous aider à améliorer le guide.',
      feedbackType: 'issue_recovery',
      options: [
        { id: 'audio_quality', label: 'Qualité audio', value: 'audio_quality' },
        { id: 'too_much_information', label: 'Trop d’informations', value: 'too_much_information' },
        { id: 'not_enough_information', label: 'Pas assez d’informations', value: 'not_enough_information' },
        { id: 'hard_to_follow', label: 'Difficile à suivre', value: 'hard_to_follow' },
        { id: 'bad_timing', label: 'Mauvais timing', value: 'bad_timing' },
        { id: 'navigation_confusion', label: 'Confusion de navigation', value: 'navigation_confusion' },
        { id: 'language_issue', label: 'Problème de langue', value: 'language_issue' },
        { id: 'content_not_relevant', label: 'Contenu non pertinent', value: 'content_not_relevant' },
        { id: 'technical_issue', label: 'Problème technique', value: 'technical_issue' },
      ],
    },
  },
};

const buildDefaultFilename = (metadata) => generateSafeGuideFilename({
  country: metadata?.country || '',
  language: metadata?.language || 'en-US',
  name: metadata?.name || ''
});

const getFeedbackLocaleKey = (language) => {
  const normalized = (language || '').toString().toLowerCase().trim();
  if (normalized.startsWith('es')) return 'es';
  if (normalized.startsWith('pt')) return 'pt';
  if (normalized.startsWith('fr')) return 'fr';
  return 'en';
};

const createDefaultFeedbackConfig = (language = 'en') => ({
  language: getFeedbackLocaleKey(language),
  ...cloneFeedbackValue(DEFAULT_FEEDBACK_CONFIGS[getFeedbackLocaleKey(language)] || DEFAULT_FEEDBACK_CONFIGS.en),
});

const isPositiveQuestionKind = (kind) => kind === 'visit_impact' || kind === 'learning_value' || kind === 'ease_of_use' || kind === 'recommendation';

const normalizeFeedbackKind = (value, fallbackKind = 'visit_impact') => {
  switch ((value || fallbackKind || '').toString().toLowerCase().trim()) {
    case 'learningvalue':
    case 'learning_value':
    case 'learning-value':
      return 'learning_value';
    case 'easeofuse':
    case 'ease_of_use':
    case 'ease-of-use':
      return 'ease_of_use';
    case 'recommendation':
    case 'would_recommend':
    case 'recommend':
      return 'recommendation';
    case 'issuepicker':
    case 'issue_picker':
    case 'issue-picker':
      return 'issue_picker';
    case 'visitimpact':
    case 'visit_impact':
    case 'visit-impact':
    default:
      return 'visit_impact';
  }
};

const normalizeFeedbackOption = (option = {}, fallbackIndex = 0) => ({
  id: option.id || option.value || `option_${fallbackIndex + 1}`,
  label: option.label || option.value || `Option ${fallbackIndex + 1}`,
  value: option.value || option.id || `option_${fallbackIndex + 1}`,
  rating: option.rating === '' || option.rating === null || option.rating === undefined ? '' : option.rating,
  advances_to_next: Boolean(option.advances_to_next || option.advance || option.positive || (option.rating !== undefined && Number(option.rating) >= 4)),
});

const normalizeFeedbackQuestion = (question = {}, fallbackKind = 'visit_impact', language = 'en') => {
  const kind = normalizeFeedbackKind(question.kind, fallbackKind);
  const defaults = createDefaultFeedbackConfig(language);
  const defaultOptions = fallbackKind === 'issue_picker'
    ? defaults.issue_question.options
    : defaults.micro_prompts[0].options;
  const base = {
    id: question.id || kind,
    kind,
    title: question.title || '',
    prompt: question.prompt || '',
    feedbackType: question.feedbackType || question.feedback_type || 'micro_prompt',
  };

  const options = Array.isArray(question.options) ? question.options : [];
  return {
    ...base,
    options: options.length > 0
      ? options.map((option, index) => normalizeFeedbackOption(option, index))
      : defaultOptions.map((option, index) => normalizeFeedbackOption(option, index)),
  };
};

const normalizeFeedbackConfig = (feedback, language = 'en') => {
  const templateLanguage = feedback?.language || language;
  const defaults = createDefaultFeedbackConfig(templateLanguage);
  if (!feedback) return defaults;

  const sourceMicroPrompts = Array.isArray(feedback.micro_prompts) ? feedback.micro_prompts.slice(0, 3) : [];
  const microPrompts = defaults.micro_prompts.map((defaultQuestion, index) =>
    normalizeFeedbackQuestion(
      sourceMicroPrompts[index] || defaultQuestion,
      defaultQuestion.kind || 'visit_impact',
      language,
    ));

  const issueQuestion = feedback.issue_question
    ? normalizeFeedbackQuestion(feedback.issue_question, 'issue_picker', templateLanguage)
    : defaults.issue_question;

  return {
    ...defaults,
    ...feedback,
    micro_prompts: microPrompts.length > 0 ? microPrompts : defaults.micro_prompts,
    issue_question: issueQuestion,
  };
};

export const MetadataSection = ({
  itinerary,
  setItinerary,
  b2bClient,
  setB2bClient,
  selectedModel,
  showKnowledgeTools = true,
  errors = {}
}) => {
  // Access control hooks
  const { hasFieldAccess, getFieldAccess } = useAccessControl();
  const isTranslatedGuide = itinerary?.metadata?.translated_itinerary === 'yes';
  const isTranslatedSharedFieldLocked = (fieldKey) =>
    isTranslatedGuide && LOCKED_TRANSLATED_METADATA_FIELDS.has(fieldKey);

  const [isGeneratingGuideDesc, setIsGeneratingGuideDesc] = useState(false);
  const [isGeneratingKnowledgeBatch, setIsGeneratingKnowledgeBatch] = useState(false);
  const [showFeedbackEditor, setShowFeedbackEditor] = useState(false);
  const feedbackTemplateLanguage = normalizeFeedbackConfig(itinerary.metadata.feedback, itinerary.metadata.language).language || getFeedbackLocaleKey(itinerary.metadata.language);
  const downloadOnLocation =
    itinerary.metadata.download_on_location ||
    (itinerary.metadata.force_download_on_location === "true" ? 'force' : 'off');
  const mapDisplaySettings = normalizeMapDisplaySettings(itinerary.metadata.map_display);

  const handleGenerateGuideDescription = async () => {
    const { name, city, country, theme, categories, tags } = itinerary.metadata || {};
    if (!name) {
      alert('Please enter the guide name first.');
      return;
    }

    setIsGeneratingGuideDesc(true);
    try {
      const location = [city, country].filter(Boolean).join(', ');
      const context = [theme, ...(categories || []), ...(tags || [])].filter(Boolean).join(', ');

      const stopSummaries = (itinerary.points || [])
        .filter(p => p.name)
        .map(p => {
          const desc = p.description ? ` — ${p.description}` : '';
          return `• ${p.name}${desc}`;
        })
        .join('\n');

      const stopsContext = stopSummaries ? `\n\nStops in this guide:\n${stopSummaries}` : '';
      const prompt = `Write a brief overall description for the guide "${name}"${location ? ` in ${location}` : ''}${context ? ` (${context})` : ''}. One paragraph, max 60 words, no title, conversational tone. Base it on the actual stops listed below.${stopsContext}`;

      const messages = [
        { role: 'system', content: 'Return ONLY the description text. No titles, no introductions.' },
        { role: 'user', content: prompt }
      ];

      const modelId = selectedModel || 'claude-sonnet-4-6';
      const result = await aiService.getCompletion(messages, modelId, { maxTokens: 150, temperature: 0.7 });

      setItinerary(prev => ({
        ...prev,
        metadata: { ...prev.metadata, description: result.trim() }
      }));
    } catch (error) {
      console.error('Failed to generate guide description:', error);
      alert(`Failed to generate description: ${error.message}`);
    } finally {
      setIsGeneratingGuideDesc(false);
    }
  };

  const handleGenerateKnowledgeForAllStops = async () => {
    const editableHistorical = hasFieldAccess('historicalContext', 'edit');
    const editableCultural = hasFieldAccess('culturalSignificance', 'edit');

    if (!editableHistorical && !editableCultural) {
      setStatus('error');
      setMessage('You do not have permission to generate supplementary content.');
      return;
    }

    if (!Array.isArray(itinerary.points) || itinerary.points.length === 0) {
      setStatus('error');
      setMessage('Add at least one stop before generating supplementary content.');
      return;
    }

    setIsGeneratingKnowledgeBatch(true);
    setStatus(null);
    setMessage('');

    try {
      const knowledgeContext = await buildKnowledgeContext(itinerary.metadata, {
        maxDocs: 20,
        maxChars: 250000,
        fullDocuments: true,
        heading: 'Uploaded knowledge documents:'
      });
      const modelId = selectedModel || 'claude-sonnet-4-6';
      const language = itinerary.metadata?.language || 'en-US';
      const location = [itinerary.metadata?.city, itinerary.metadata?.country].filter(Boolean).join(', ');
      const stopList = itinerary.points.map((point, index) => {
        const stopId = String(point.id || `stop-${index + 1}`);
        return [
          `STOP_ID: ${stopId}`,
          `Stop name: ${point.name || 'Unnamed stop'}`,
          `Location: ${location || 'Unknown location'}`,
          point.description ? `Stop summary: ${point.description}` : '',
          point.category ? `Category: ${point.category}` : '',
          point.hiddenGems ? `Known details: ${point.hiddenGems}` : '',
          point.coordinates?.latitude && point.coordinates?.longitude
            ? `Coordinates: ${point.coordinates.latitude}, ${point.coordinates.longitude}`
            : ''
        ].filter(Boolean).join('\n');
      }).join('\n\n---\n\n');

      const userPrompt = [
        knowledgeContext ? `Knowledge source:\n${knowledgeContext}` : '',
        'Generate supplementary content for every stop below in one response.',
        'Return exactly this tagged structure for each stop and nothing else:',
        '[[STOP:STOP_ID]]',
        '[[HISTORICAL_CONTEXT]]',
        '- bullet 1',
        '- bullet 2',
        '- bullet 3',
        '[[/HISTORICAL_CONTEXT]]',
        '[[CULTURAL_SIGNIFICANCE]]',
        '- bullet 1',
        '- bullet 2',
        '- bullet 3',
        '[[/CULTURAL_SIGNIFICANCE]]',
        'Repeat that structure for every stop using its real STOP_ID.',
        'Each section must contain 3 or 4 short bullet points.',
        'Each bullet must be one short sentence.',
        'Use details that are specific to each stop, not generic facts about the wider area.',
        'If the source does not support a fact for a stop, leave that section empty.',
        'Use only facts supported by the knowledge source. Do not invent anything.',
        `Write in ${language}.`,
        `Stops:\n${stopList}`
      ].filter(Boolean).join('\n\n');

      const messages = [
        {
          role: 'system',
          content: 'Return only the requested tagged sections for all stops. No JSON, no markdown fences, no explanations.'
        },
        {
          role: 'user',
          content: userPrompt
        }
      ];

      const raw = await aiService.getCompletion(messages, modelId, {
        maxTokens: 6000,
        temperature: 0.3
      });

      let parsedByStop;
      try {
        parsedByStop = parseBatchSupplementaryContentResponse(
          raw,
          itinerary.points.map((point, index) => String(point.id || `stop-${index + 1}`))
        );
      } catch (parseError) {
        console.error('Batch supplementary content parse failed', {
          rawResponse: raw,
          parseError: parseError.message
        });
        throw parseError;
      }

      const nextPoints = itinerary.points.map((point, index) => {
        const stopId = String(point.id || `stop-${index + 1}`);
        const parsed = parsedByStop[stopId] || {};

        return {
          ...point,
          historicalContext: editableHistorical ? (parsed.historicalContext || point.historicalContext || '') : (point.historicalContext || ''),
          culturalSignificance: editableCultural ? (parsed.culturalSignificance || point.culturalSignificance || '') : (point.culturalSignificance || '')
        };
      });

      setItinerary(prev => ({
        ...prev,
        points: nextPoints,
        metadata: {
          ...prev.metadata,
          updatedAt: new Date().toISOString()
        }
      }));

      setStatus('success');
      setMessage(
        `Generated supplementary content for ${nextPoints.filter(point => point.historicalContext || point.culturalSignificance).length} stops`
      );
    } catch (error) {
      console.error('Failed to generate knowledge-based content for all stops:', error);
      setStatus('error');
      setMessage(`Failed to generate supplementary content: ${error.message}`);
    } finally {
      setIsGeneratingKnowledgeBatch(false);
    }
  };

  const [status, setStatus] = useState(null); // 'success', 'error', null
  const [message, setMessage] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [customFilename, setCustomFilename] = useState('');
  const [filenameValidation, setFilenameValidation] = useState({ isValid: true, issues: [] });

  // State to track if we showed the missing field warning
  const [hasShownMissingFieldWarning, setHasShownMissingFieldWarning] = useState(false);

  // Validate filename when metadata changes
  useEffect(() => {
    const candidateFilename = customFilename || itinerary.metadata.filename || buildDefaultFilename(itinerary.metadata);
    if (candidateFilename) {
      const validation = validateFilename(candidateFilename);
      setFilenameValidation(validation);
    }
  }, [customFilename, itinerary.metadata, itinerary.metadata.filename, itinerary.metadata.name, itinerary.metadata.country, itinerary.metadata.language]);

  // Metadata ID validation functions
  const validateMetadataId = (id) => {
    if (!id) return false; // No ID
    const timestampPattern = /^\d{13}$/; // 13-digit timestamp
    return timestampPattern.test(id);
  };

  const fixMetadataId = () => {
    const currentId = itinerary.metadata?.id;
    let newId;

    if (!currentId) {
      // No ID - generate new timestamp
      newId = Date.now().toString();
    } else if (currentId.includes('_')) {
      // ID with underscore - extract timestamp part
      newId = currentId.split('_')[0];
      // Validate the extracted part is a 13-digit number
      if (!/^\d{13}$/.test(newId)) {
        newId = Date.now().toString();
      }
    } else if (!/^\d{13}$/.test(currentId)) {
      // Invalid format - generate new timestamp
      newId = Date.now().toString();
    } else {
      // Already valid
      return;
    }

    // Update the metadata
    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        id: newId
      }
    }));
  };

  const isMetadataIdValid = validateMetadataId(itinerary.metadata?.id);

  // Check for missing isIntroductoryStop field when component mounts or itinerary changes
  useEffect(() => {
    if (itinerary.metadata && !hasShownMissingFieldWarning) {
      // Check if the field was marked as missing during load
      if (itinerary.metadata._hadIntroductoryStopField === false) {
        setStatus('error');
        setMessage('Warning: This file was missing the "Introduction by Sofia" setting. It has been set to disabled by default.');
        setHasShownMissingFieldWarning(true);
      }
    }
  }, [itinerary.metadata, hasShownMissingFieldWarning]);

  useEffect(() => {
    if (!itinerary.metadata.feedbackCollect) {
      setShowFeedbackEditor(false);
      return;
    }

    const feedback = itinerary.metadata.feedback;
    const hasMicroPrompts = Array.isArray(feedback?.micro_prompts) && feedback.micro_prompts.length > 0;
    const hasIssueQuestion = Boolean(feedback?.issue_question);

    if (hasMicroPrompts && hasIssueQuestion) {
      return;
    }

    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        feedback: normalizeFeedbackConfig(prev.metadata.feedback, prev.metadata.language),
      },
    }));
  }, [itinerary.metadata.feedbackCollect, itinerary.metadata.feedback, setItinerary]);

  // Enhanced validation that includes isIntroductoryStop
  const validateAllRequiredFields = () => {
    const metadata = itinerary.metadata;
    const issues = [];

    // Check basic required fields (only if visible)
    if (getFieldAccess('itinerary_name') !== 'hidden' && !metadata.name) issues.push('Itinerary Name');
    if (getFieldAccess('theme') !== 'hidden' && !metadata.theme) issues.push('Theme');
    if (getFieldAccess('country') !== 'hidden' && !metadata.country) issues.push('Country');
    if (getFieldAccess('city') !== 'hidden' && !metadata.city) issues.push('City');
    if (getFieldAccess('data_source') !== 'hidden' && !metadata.data_source) issues.push('Data Source');
    if (getFieldAccess('language') !== 'hidden' && !metadata.language) issues.push('Content Language');
    if (getFieldAccess('itinerary_duration') !== 'hidden' && !metadata.duration) issues.push('Duration');
    if (getFieldAccess('itinerary_difficulty') !== 'hidden' && !metadata.difficulty) issues.push('Difficulty');
    if (getFieldAccess('itinerary_description') !== 'hidden' && !metadata.description) issues.push('Description');
    if (getFieldAccess('thumbnailUrl') !== 'hidden' && !metadata.thumbnailUrl) issues.push('Thumbnail Image URL');

    // Check coordinates
    if (getFieldAccess('itinerary_coordinates') !== 'hidden' &&
        (!metadata.coordinates?.latitude || !metadata.coordinates?.longitude)) {
      issues.push('Location Coordinates');
    }

    // Check categories and tags
    if (getFieldAccess('itinerary_categories') !== 'hidden' &&
        (!metadata.categories || metadata.categories.length === 0 ||
        (metadata.categories.length === 1 && metadata.categories[0] === ''))) {
      issues.push('Categories');
    }

    if (getFieldAccess('itinerary_tags') !== 'hidden' &&
        (!metadata.tags || metadata.tags.length === 0 ||
        (metadata.tags.length === 1 && metadata.tags[0] === ''))) {
      issues.push('Tags');
    }

    // IMPORTANT: Check isIntroductoryStop field
    if (getFieldAccess('isIntroductoryStop') !== 'hidden' &&
        (metadata.isIntroductoryStop === undefined || metadata.isIntroductoryStop === null)) {
      issues.push('Introduction by Sofia setting (must be checked or unchecked)');
    }

    if (getFieldAccess('guide_type') !== 'hidden' && !metadata.guide_type) issues.push('Guide Type');

    // Check 360visitUrl is required when 360visit is enabled
    if (metadata['360visit'] === true && !metadata['360visitUrl']) {
      issues.push('360° Visit URL (required when 360° Visit is enabled)');
    }

    return issues;
  };


  // Function to handle coordinate changes
  const handleCoordinateChange = (field, value) => {
    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        coordinates: {
          ...prev.metadata.coordinates,
          [field]: value
        }
      }
    }));
  };

  // Enhanced checkbox change handler with validation
  const handleIntroductoryStopChange = (checked) => {
    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        isIntroductoryStop: checked,
        // Clear the missing field flag since user is now setting it explicitly
        _hadIntroductoryStopField: true
      }
    }));

    // Clear any warning messages when user sets the field
    if (status === 'error' && message.includes('Introduction by Sofia')) {
      setStatus(null);
      setMessage('');
    }
  };

  useEffect(() => {
    if (itinerary.metadata.filename) {
      setCustomFilename(itinerary.metadata.filename);
      return;
    }

    if (itinerary.metadata.country && itinerary.metadata.name) {
      const defaultFilename = buildDefaultFilename(itinerary.metadata);
      setCustomFilename(defaultFilename);
      setItinerary(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          filename: defaultFilename
        }
      }));
    }
  }, [itinerary.metadata.filename, itinerary.metadata.country, itinerary.metadata.language, itinerary.metadata.name, setItinerary]);


  // New function to auto-generate tags and categories
  const handleAutoGenerateTags = () => {
    setIsGeneratingTags(true);

    try {
      // Only proceed if there are POIs
      if (itinerary.points && itinerary.points.length > 0) {
        const { tags, categories } = generateTagsAndCategories(itinerary.points);

        setItinerary(prev => ({
          ...prev,
          metadata: {
            ...prev.metadata,
            // Add auto-generated tags and categories, keep any existing ones
            tags: [...new Set([...tags, ...(prev.metadata.tags || [])])],
            categories: [...new Set([...categories, ...(prev.metadata.categories || [])])]
          }
        }));

        setStatus('success');
        setMessage('Tags and categories generated from POIs');
      } else {
        setStatus('error');
        setMessage('No POIs found to generate tags from');
      }
    } catch (error) {
      console.error('Error generating tags:', error);
      setStatus('error');
      setMessage('Failed to generate tags');
    } finally {
      setIsGeneratingTags(false);
    }
  };

  const updateFeedbackMetadata = (updater) => {
    setItinerary(prev => {
      const currentFeedback = normalizeFeedbackConfig(prev.metadata.feedback, prev.metadata.language);
      const nextFeedback = updater(currentFeedback);
      return {
        ...prev,
        metadata: {
          ...prev.metadata,
          feedback: nextFeedback,
          updatedAt: new Date().toISOString(),
        },
      };
    });
  };

  const resetFeedbackToDefaults = () => {
    updateFeedbackMetadata(() => createDefaultFeedbackConfig(feedbackTemplateLanguage));
  };

  const updateMicroPrompt = (index, updater) => {
    updateFeedbackMetadata((feedback) => {
      const microPrompts = [...feedback.micro_prompts];
      if (!microPrompts[index]) return feedback;
      microPrompts[index] = updater(microPrompts[index]);
      return { ...feedback, micro_prompts: microPrompts };
    });
  };

  const addMicroPrompt = () => {
    updateFeedbackMetadata((feedback) => {
      if (feedback.micro_prompts.length >= 3) return feedback;
      const defaults = createDefaultFeedbackConfig(feedbackTemplateLanguage);
      const template = defaults.micro_prompts[feedback.micro_prompts.length] || defaults.micro_prompts[0];
      return {
        ...feedback,
        micro_prompts: [...feedback.micro_prompts, cloneFeedbackValue(template)],
      };
    });
  };

  const removeMicroPrompt = (index) => {
    updateFeedbackMetadata((feedback) => {
      const microPrompts = feedback.micro_prompts.filter((_, currentIndex) => currentIndex !== index);
      return {
        ...feedback,
        micro_prompts: microPrompts,
      };
    });
  };

  const updateIssueQuestion = (updater) => {
    updateFeedbackMetadata((feedback) => ({
      ...feedback,
      issue_question: updater(feedback.issue_question),
    }));
  };

  const updateQuestionOption = (questionIndex, optionIndex, updater) => {
    updateMicroPrompt(questionIndex, (question) => {
      const options = [...question.options];
      if (!options[optionIndex]) return question;
      options[optionIndex] = updater(options[optionIndex]);
      return { ...question, options };
    });
  };

  const addQuestionOption = (questionIndex) => {
    updateMicroPrompt(questionIndex, (question) => ({
      ...question,
      options: [
        ...question.options,
        {
          id: `option_${question.options.length + 1}`,
          label: `Option ${question.options.length + 1}`,
          value: `option_${question.options.length + 1}`,
          rating: '',
          advances_to_next: false,
        },
      ],
    }));
  };

  const removeQuestionOption = (questionIndex, optionIndex) => {
    updateMicroPrompt(questionIndex, (question) => ({
      ...question,
      options: question.options.filter((_, currentIndex) => currentIndex !== optionIndex),
    }));
  };

  const questionKindOptions = [
    { value: 'visit_impact', label: 'Visit impact' },
    { value: 'learning_value', label: 'Learning value' },
    { value: 'ease_of_use', label: 'Ease of use' },
    { value: 'recommendation', label: 'Recommendation' },
  ];

  // Validation function that can be called from outside
  const validateBeforeSave = () => {
    const issues = validateAllRequiredFields();
    if (issues.length > 0) {
      setStatus('error');
      setMessage(`Missing required fields: ${issues.join(', ')}`);
      return false;
    }
    return true;
  };

  return (
    <div className="border-b pb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Itinerary Metadata</h2>
        {itinerary.metadata?.id ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">ID:</span>
            <code className={`px-2 py-1 rounded font-mono text-xs ${
              isMetadataIdValid
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-red-100 text-red-700 border border-red-200'
            }`}>
              {itinerary.metadata.id}
            </code>
            {!isMetadataIdValid && (
              <button
                onClick={fixMetadataId}
                className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 flex items-center gap-1"
                title="Fix ID format - extract timestamp or generate new one"
              >
                <RefreshCw className="w-3 h-3" />
                Fix
              </button>
            )}
            {isMetadataIdValid && (
              <div className="text-green-600" title="Valid timestamp format">
                <Check className="w-3 h-3" />
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">ID:</span>
            <span className="text-red-600 text-xs">Missing</span>
            <button
              onClick={fixMetadataId}
              className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 flex items-center gap-1"
              title="Generate new timestamp ID"
            >
              <RefreshCw className="w-3 h-3" />
              Generate
            </button>
          </div>
        )}
      </div>

      {/* --- Status Messages --- */}
      {status === 'success' && (
        <div className="mb-4 flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded border border-green-200">
          <Check className="w-4 h-4" />
          <span>{message}</span>
        </div>
      )}
      {status === 'error' && (
        <div className="mb-4 flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded border border-red-200">
          <AlertCircle className="w-4 h-4" />
          <div>
            <p className="font-medium">Validation Error:</p>
            <p className="text-sm">{message}</p>
          </div>
        </div>
      )}

      {/* --- Group: meta-basic --- */}
      <CollapsibleSection id="meta-basic" title="Core Information" summary="Name, country, city, language, thumbnail" defaultOpen>

        {/* Thumbnail Image */}
        {getFieldAccess('thumbnailUrl') !== 'hidden' && (
          <div className="mb-6">
            {itinerary.metadata?.venue_page?.logoUrl && (
              <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
                <p className="mb-2 text-sm font-medium text-slate-700">Business Logo</p>
                <div className="flex h-24 w-full items-center justify-center rounded-lg bg-slate-50 p-3">
                  <img
                    src={itinerary.metadata.venue_page.logoUrl}
                    alt="Business logo"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              </div>
            )}
            <ImageUploader
              imageUrl={itinerary.metadata.thumbnailUrl}
              attributionData={itinerary.metadata.thumbnailAttribution}
              onImageUploaded={(url, ccData) => setItinerary(prev => ({
                ...prev,
                metadata: {
                  ...prev.metadata,
                  thumbnailUrl: url,
                  thumbnailAttribution: ccData || null
                }
              }))}
              itineraryMetadata={{
                country: itinerary.metadata.country,
                name: itinerary.metadata.name,
                filename: itinerary.metadata.filename
              }}
              label="Thumbnail Image"
              maxSizeKB={400}
              disabled={(() => {
                const access = getFieldAccess('thumbnailUrl');
                return (access !== 'edit' && access !== 'full') || isTranslatedSharedFieldLocked('thumbnailUrl');
              })()}
            />
          </div>
        )}

        {/* Name & Theme */}
        {(getFieldAccess('itinerary_name') !== 'hidden' || getFieldAccess('theme') !== 'hidden') && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            {getFieldAccess('itinerary_name') !== 'hidden' && (
              <div>
                <label className="block text-sm font-medium mb-1">Display Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded ${!itinerary.metadata.name ? 'border-yellow-500' : ''}`}
                  placeholder="Enter display name"
                  value={itinerary.metadata.name || ''}
                  onChange={(e) => setItinerary(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, name: e.target.value }
                  }))}
                  disabled={!hasFieldAccess('itinerary_name', 'edit')}
                />
                <p className="text-xs text-gray-500 mt-1">Shown to users. Changing this does not rename the saved JSON file or storage folder.</p>

                {/* Filename validation warning */}
                {customFilename && !filenameValidation.isValid && (
                  <div className="mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                    <div className="flex items-center gap-1 text-yellow-700 font-medium mb-1">
                      <AlertCircle className="w-4 h-4" />
                      Filename Issues Detected
                    </div>
                    <ul className="text-yellow-600 text-xs space-y-1">
                      {filenameValidation.issues.map((issue, index) => (
                        <li key={index}>• {issue}</li>
                      ))}
                    </ul>
                    <div className="mt-2 text-xs text-yellow-600">
                      <strong>Current save filename:</strong> {customFilename}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {getFieldAccess('theme') !== 'hidden' && (
              <div>
                <label className="block text-sm font-medium mb-1">Theme <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded ${!itinerary.metadata.theme ? 'border-yellow-500' : ''}`}
                  placeholder="Enter theme"
                  value={itinerary.metadata.theme || ''}
                  onChange={(e) => setItinerary(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, theme: e.target.value }
                  }))}
                  disabled={!hasFieldAccess('theme', 'edit') || isTranslatedSharedFieldLocked('theme')}
                />
              </div>
            )}
          </div>
        )}

        {/* Country & City */}
        {(getFieldAccess('country') !== 'hidden' || getFieldAccess('city') !== 'hidden') && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            {getFieldAccess('country') !== 'hidden' && (
              <div>
                <label className="block text-sm font-medium mb-1">Country <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded ${!itinerary.metadata.country ? 'border-yellow-500' : ''}`}
                  placeholder="Enter country"
                  value={itinerary.metadata.country || ''}
                  onChange={(e) => setItinerary(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, country: e.target.value }
                  }))}
                  disabled={!hasFieldAccess('country', 'edit') || isTranslatedSharedFieldLocked('country')}
                />
              </div>
            )}

            {getFieldAccess('city') !== 'hidden' && (
              <div>
                <label className="block text-sm font-medium mb-1">City <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded ${!itinerary.metadata.city ? 'border-yellow-500' : ''}`}
                  placeholder="Enter city"
                  value={itinerary.metadata.city || ''}
                  onChange={(e) => setItinerary(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, city: e.target.value }
                  }))}
                  disabled={!hasFieldAccess('city', 'edit') || isTranslatedSharedFieldLocked('city')}
                />
              </div>
            )}
          </div>
        )}

        {/* Language Selection */}
        {getFieldAccess('language') !== 'hidden' && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Content Language <span className="text-red-500">*</span></label>
            <LanguageSelector
              selectedLanguage={itinerary.metadata.language || 'en'}
              onLanguageChange={(language) => setItinerary(prev => ({
                ...prev,
                metadata: { ...prev.metadata, language: language }
              }))}
              disabled={!hasFieldAccess('language', 'edit')}
            />
            {!hasFieldAccess('language', 'edit') && (
              <p className="text-xs text-gray-500 mt-1">(View Only Mode)</p>
            )}
            <p className="text-xs text-gray-600 mt-1">
              This determines the language for AI content generation and text-to-speech voice selection.
            </p>
          </div>
        )}

        {/* Custom Filename - Moved from File & Translation section */}
        {getFieldAccess('filename') !== 'hidden' && (
          <div>
            <label className="block text-sm font-medium mb-1">Save Filename <span className="text-red-500">*</span></label>
            <div className="flex gap-2">
              <input
                type="text"
                className={`w-full p-2 border rounded ${!customFilename ? 'border-yellow-500' : ''}`}
                placeholder="country_language_name.json"
                value={customFilename}
                onChange={(e) => {
                  setCustomFilename(e.target.value);
                  setItinerary(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, filename: e.target.value }
                  }));
                }}
                disabled={!hasFieldAccess('filename', 'edit')}
              />
              {hasFieldAccess('filename', 'edit') && (
                <button
                  onClick={() => {
                    if (itinerary.metadata.country && itinerary.metadata.name) {
                      const filename = buildDefaultFilename(itinerary.metadata);
                      setCustomFilename(filename);
                      setItinerary(prev => ({
                        ...prev,
                        metadata: { ...prev.metadata, filename: filename }
                      }));
                    }
                  }}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center gap-1"
                  title="Reset to default format"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">Stable storage key. Reset only if you intentionally want a new file/folder path.</p>
          </div>
        )}
      </CollapsibleSection>

      {/* --- Group: Itinerary Details --- */}
      {(getFieldAccess('itinerary_duration') !== 'hidden' || getFieldAccess('itinerary_difficulty') !== 'hidden' || getFieldAccess('itinerary_rating') !== 'hidden' || getFieldAccess('itinerary_description') !== 'hidden' || getFieldAccess('guide_type') !== 'hidden') && (
        <CollapsibleSection id="meta-details" title="Itinerary Details" summary="Duration, difficulty, rating, description" className="mt-6" defaultOpen>

          {/* Duration, Difficulty, Rating */}
          {(getFieldAccess('itinerary_duration') !== 'hidden' || getFieldAccess('itinerary_difficulty') !== 'hidden' || getFieldAccess('itinerary_rating') !== 'hidden') && (
            <div className="grid grid-cols-3 gap-4">
              {getFieldAccess('itinerary_duration') !== 'hidden' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Duration (days) <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    className={`w-full p-2 border rounded ${!itinerary.metadata.duration ? 'border-yellow-500' : ''}`}
                    placeholder="Enter duration in days"
                    value={itinerary.metadata.duration || ''}
                    onChange={(e) => setItinerary(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, duration: e.target.value }
                    }))}
                    disabled={!hasFieldAccess('itinerary_duration', 'edit') || isTranslatedSharedFieldLocked('itinerary_duration')}
                  />
                </div>
              )}
              
              {getFieldAccess('itinerary_difficulty') !== 'hidden' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Difficulty <span className="text-red-500">*</span></label>
                  <select
                    className={`w-full p-2 border rounded ${!itinerary.metadata.difficulty ? 'border-yellow-500' : ''}`}
                    value={itinerary.metadata.difficulty || 'easy'}
                    onChange={(e) => setItinerary(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, difficulty: e.target.value }
                    }))}
                    disabled={!hasFieldAccess('itinerary_difficulty', 'edit') || isTranslatedSharedFieldLocked('itinerary_difficulty')}
                  >
                    <option value="easy">Easy</option>
                    <option value="moderate">Moderate</option>
                    <option value="challenging">Challenging</option>
                  </select>
                </div>
              )}
              
              {getFieldAccess('itinerary_rating') !== 'hidden' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Rating</label>
                  <input
                    type="number"
                    step="0.1"
                    className="w-full p-2 border rounded"
                    placeholder="e.g., 4.5"
                    value={itinerary.metadata.rating || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setItinerary(prev => ({
                        ...prev,
                        metadata: { ...prev.metadata, rating: value ? parseFloat(value) : null }
                      }));
                    }}
                    disabled={!hasFieldAccess('itinerary_rating', 'edit')}
                  />
                </div>
              )}
              </div>
            )}

            {getFieldAccess('data_source') !== 'hidden' && (
              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">
                  Data Source <span className="text-red-500">*</span>
                </label>
                <textarea
                  className={`w-full p-2 border rounded ${!itinerary.metadata.data_source ? 'border-yellow-500' : ''}`}
                  rows={3}
                  placeholder="Client source, ChatGPT research, Claude research, archival notes, museum brief, and so on"
                  value={itinerary.metadata.data_source || ''}
                  onChange={(e) => setItinerary(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, data_source: e.target.value }
                  }))}
                  disabled={!hasFieldAccess('data_source', 'edit') || isTranslatedSharedFieldLocked('data_source')}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Record the origin of the content used to build this itinerary.
                </p>
              </div>
            )}

            {/* Description */}
            {getFieldAccess('itinerary_description') !== 'hidden' && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium">Description <span className="text-red-500">*</span></label>
                {hasFieldAccess('itinerary_description', 'edit') && (
                  <button
                    type="button"
                    onClick={handleGenerateGuideDescription}
                    disabled={isGeneratingGuideDesc}
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-teal-500 text-white rounded hover:bg-teal-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {isGeneratingGuideDesc ? (
                      <><Loader className="w-3 h-3 animate-spin" /> Generating...</>
                    ) : (
                      'Generate'
                    )}
                  </button>
                )}
              </div>
              <textarea
                className={`w-full p-2 border rounded ${!itinerary.metadata.description ? 'border-yellow-500' : ''}`}
                rows={4}
                placeholder="Enter itinerary description"
                value={itinerary.metadata.description || ''}
                onChange={(e) => setItinerary(prev => ({
                  ...prev,
                  metadata: { ...prev.metadata, description: e.target.value }
                }))}
                disabled={!hasFieldAccess('itinerary_description', 'edit')}
              />
            </div>
          )}

          {showKnowledgeTools && (
            <>
              <FeatureGuard featureName="metadata" requiredAccess="edit">
                <div className="mt-4">
                  <KnowledgeDocPanel itinerary={itinerary} setItinerary={setItinerary} />
                </div>
              </FeatureGuard>

              {(hasFieldAccess('historicalContext', 'edit') || hasFieldAccess('culturalSignificance', 'edit')) && (
                <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                  <div>
                    <p className="text-sm font-medium text-indigo-900">Batch supplementary generation</p>
                    <p className="text-xs text-indigo-700">
                      Fill historical context and cultural significance for every stop from the uploaded knowledge documents.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleGenerateKnowledgeForAllStops}
                    disabled={isGeneratingKnowledgeBatch}
                    className="flex items-center gap-2 px-3 py-2 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {isGeneratingKnowledgeBatch ? (
                      <>
                        <Loader className="w-3 h-3 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      'Generate for all stops'
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </CollapsibleSection>
      )}

      {/* --- Group: Location Coordinates --- */}
      {getFieldAccess('itinerary_coordinates') !== 'hidden' && (
        <CollapsibleSection
          id="location-coordinates"
          icon={MapPin}
          title={<>Location Coordinates <span className="text-red-500">*</span></>}
          summary="Latitude / longitude with map search"
          className="mt-6"
        >
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Latitude <span className="text-red-500">*</span></label>
              <input
                type="text"
                className={`w-full p-2 border rounded ${!itinerary.metadata.coordinates?.latitude ? 'border-yellow-500' : ''}`}
                placeholder="Enter latitude"
                value={itinerary.metadata.coordinates?.latitude || ''}
                onChange={(e) => handleCoordinateChange('latitude', e.target.value)}
                disabled={!hasFieldAccess('itinerary_coordinates', 'edit') || isTranslatedSharedFieldLocked('itinerary_coordinates')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Longitude <span className="text-red-500">*</span></label>
              <input
                type="text"
                className={`w-full p-2 border rounded ${!itinerary.metadata.coordinates?.longitude ? 'border-yellow-500' : ''}`}
                placeholder="Enter longitude"
                value={itinerary.metadata.coordinates?.longitude || ''}
                onChange={(e) => handleCoordinateChange('longitude', e.target.value)}
                disabled={!hasFieldAccess('itinerary_coordinates', 'edit') || isTranslatedSharedFieldLocked('itinerary_coordinates')}
              />
            </div>
          </div>

          {/* Guide Type - Moved from Itinerary Details section */}
          {getFieldAccess('guide_type') !== 'hidden' && (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Guide Type <span className="text-red-500">*</span></label>
              <select
                className="w-full p-2 border rounded"
                value={itinerary.metadata.guide_type || ''}
                onChange={(e) => setItinerary(prev => ({
                  ...prev,
                  metadata: {
                    ...prev.metadata,
                    guide_type: e.target.value,
                    auto_play_radius_meters: e.target.value === 'bus_tour'
                      ? (parseInt(prev.metadata.auto_play_radius_meters, 10) || 50)
                      : prev.metadata.auto_play_radius_meters
                  }
                }))}
                disabled={!hasFieldAccess('guide_type', 'edit') || isTranslatedSharedFieldLocked('guide_type')}
              >
                <option value="">Select guide type</option>
                <option value="indoor">Indoor</option>
                <option value="outdoor">Outdoor</option>
                <option value="hybrid">Hybrid</option>
                <option value="bus_tour">Bus Tour</option>
              </select>
            </div>
          )}

          <div className="mt-2">
            <button
              onClick={() => setShowMap(!showMap)}
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1 text-sm"
              disabled={!hasFieldAccess('itinerary_coordinates', 'edit') || isTranslatedSharedFieldLocked('itinerary_coordinates')}
            >
              <MapPin className="w-3 h-3" />
              {showMap ? 'Hide Map' : 'Find Coordinates for ' + (itinerary.metadata.city || 'City')}
            </button>
          </div>

          {showMap && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
              <SimplifiedMapWithSearch
                latitude={itinerary.metadata.coordinates?.latitude || ''}
                longitude={itinerary.metadata.coordinates?.longitude || ''}
                onCoordinateChange={isTranslatedSharedFieldLocked('itinerary_coordinates') ? () => {} : handleCoordinateChange}
              />
              <p className="text-xs text-gray-500 mt-2">
                Search for the city or click on the map to place a marker, then extract the coordinates.
              </p>
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* --- Group: Taxonomy (Categories & Tags) --- */}
      {(getFieldAccess('itinerary_categories') !== 'hidden' || getFieldAccess('itinerary_tags') !== 'hidden') && (
        <CollapsibleSection id="taxonomy" title="Classification" summary="Categories and tags" className="mt-6">
          <div className="grid grid-cols-2 gap-4">
            {getFieldAccess('itinerary_categories') !== 'hidden' && (
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium">Categories (comma-separated) <span className="text-red-500">*</span></label>
                  {hasFieldAccess('itinerary_categories', 'edit') && !isTranslatedSharedFieldLocked('itinerary_categories') && (
                    <button
                      onClick={handleAutoGenerateTags}
                      disabled={isGeneratingTags || !itinerary.points || itinerary.points.length === 0}
                      className="px-2 py-1 bg-purple-500 text-white rounded text-xs hover:bg-purple-600 disabled:bg-gray-400 flex items-center gap-1"
                      title="Auto-generate tags and categories from POIs"
                    >
                      {isGeneratingTags ? <Loader className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      Auto-Generate
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  className={`w-full p-2 border rounded ${!itinerary.metadata.categories || itinerary.metadata.categories.length === 0 ? 'border-yellow-500' : ''}`}
                  placeholder="Historical, Cultural, Walking Tour"
                  value={itinerary.metadata.categories ? itinerary.metadata.categories.join(', ') : ''}
                  onChange={(e) => setItinerary(prev => ({
                    ...prev,
                    metadata: {
                      ...prev.metadata,
                      categories: e.target.value.split(',').map(cat => cat.trim())
                    }
                  }))}
                  disabled={!hasFieldAccess('itinerary_categories', 'edit') || isTranslatedSharedFieldLocked('itinerary_categories')}
                />
              </div>
            )}
            
            {getFieldAccess('itinerary_tags') !== 'hidden' && (
              <div>
                <label className="block text-sm font-medium mb-1">Tags (comma-separated) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded ${!itinerary.metadata.tags || itinerary.metadata.tags.length === 0 ? 'border-yellow-500' : ''}`}
                  placeholder="History, Culture, Landmarks"
                  value={itinerary.metadata.tags ? itinerary.metadata.tags.join(', ') : ''}
                  onChange={(e) => setItinerary(prev => ({
                    ...prev,
                    metadata: {
                      ...prev.metadata,
                      tags: e.target.value.split(',').map(tag => tag.trim())
                    }
                  }))}
                  disabled={!hasFieldAccess('itinerary_tags', 'edit') || isTranslatedSharedFieldLocked('itinerary_tags')}
                />
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* --- Group: AI & Sofia Features --- */}
      {(getFieldAccess('isIntroductoryStop') !== 'hidden' || getFieldAccess('ai_generated_personalization') !== 'hidden') && (
        <CollapsibleSection id="ai-sofia-features" title="AI & Sofia Features" summary="Sofia introduction, AI personalization" className="mt-6">

          <div className="space-y-4">
            {/* Introduction by Sofia */}
            {getFieldAccess('isIntroductoryStop') !== 'hidden' && (
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="isIntroductoryStop"
                  checked={Boolean(itinerary.metadata.isIntroductoryStop)}
                  onChange={(e) => {
                    if (isTranslatedSharedFieldLocked('isIntroductoryStop')) return;
                    handleIntroductoryStopChange(e.target.checked);
                  }}
                  className="w-4 h-4 mr-3 mt-0.5"
                  disabled={!hasFieldAccess('isIntroductoryStop', 'edit') || isTranslatedSharedFieldLocked('isIntroductoryStop')}
                />
                <div className="flex-1">
                  <label htmlFor="isIntroductoryStop" className="text-sm font-medium cursor-pointer">
                    Introduction by Sofia <span className="text-red-500 ml-1">*</span>
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Sofia will make a small introduction in the language of this itinerary.
                  </p>
                  {itinerary.metadata.isIntroductoryStop === undefined || itinerary.metadata.isIntroductoryStop === null ? (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <div className="flex items-center gap-1 text-yellow-700">
                        <AlertCircle className="w-3 h-3" />
                        <span className="font-medium">Required:</span> Please check or uncheck this option.
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Sofia introduction: {itinerary.metadata.isIntroductoryStop ? 'Enabled' : 'Disabled'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* AI Generated Personalization */}
            {getFieldAccess('ai_generated_personalization') !== 'hidden' && (
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="aiGeneratedPersonalization"
                  checked={Boolean(itinerary.metadata.ai_generated_personalization)}
                  onChange={(e) => setItinerary(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, ai_generated_personalization: e.target.checked }
                  }))}
                  className="w-4 h-4 mr-3 mt-0.5"
                  disabled={!hasFieldAccess('ai_generated_personalization', 'edit')}
                />
                <div className="flex-1">
                  <label htmlFor="aiGeneratedPersonalization" className="text-sm font-medium cursor-pointer">
                    AI Generated Personalization TAB present?
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Enable AI to generate personalized content for this guide.
                  </p>

                  {/* Free Users Can Generate - Only show when AI personalization is enabled */}
                  {Boolean(itinerary.metadata.ai_generated_personalization) && (
                    <div className="mt-3 ml-2 p-2 bg-blue-100 border border-blue-200 rounded">
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          id="aiPersonalizationFreeUsers"
                          checked={Boolean(itinerary.metadata.ai_generated_personalization_opentoFree)}
                          onChange={(e) => setItinerary(prev => ({
                            ...prev,
                            metadata: { ...prev.metadata, ai_generated_personalization_opentoFree: e.target.checked }
                          }))}
                          className="w-3 h-3 mr-2 mt-0.5"
                          disabled={!hasFieldAccess('ai_generated_personalization', 'edit')}
                        />
                        <div className="flex-1">
                          <label htmlFor="aiPersonalizationFreeUsers" className="text-xs font-medium cursor-pointer">
                            Free users can generate personalized content
                          </label>
                          <p className="text-xs text-gray-500 mt-1">
                            Allow free users to access AI personalization for this guide.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* --- Group: Guide Configuration & Features --- */}
      <CollapsibleSection id="guide-configuration" title="Guide Configuration & Features" summary="QR codes, map display, feedback, advanced options" className="mt-6">

          <div className="space-y-4">
          {/* Has QR Codes */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="hasQrCodes"
              checked={Boolean(itinerary.metadata.has_qr_codes)}
              onChange={(e) => !isTranslatedSharedFieldLocked('has_qr_codes') && setItinerary(prev => ({
                ...prev,
                metadata: { ...prev.metadata, has_qr_codes: e.target.checked }
              }))}
              className="w-4 h-4 mr-3 mt-0.5"
              disabled={isTranslatedSharedFieldLocked('has_qr_codes')}
            />
            <div className="flex-1">
              <label htmlFor="hasQrCodes" className="text-sm font-medium cursor-pointer">
                Has QR Codes
              </label>
              <p className="text-xs text-gray-600 mt-1">
                Indicates if this itinerary includes QR codes for users to scan.
              </p>
            </div>
          </div>

          {/* Orientation Guidance */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="orientationGuidance"
              checked={Boolean(itinerary.metadata.orientation)}
              onChange={(e) => !isTranslatedSharedFieldLocked('orientation') && setItinerary(prev => ({
                ...prev,
                metadata: { ...prev.metadata, orientation: e.target.checked }
              }))}
              className="w-4 h-4 mr-3 mt-0.5"
              disabled={isTranslatedSharedFieldLocked('orientation')}
            />
            <div className="flex-1">
              <label htmlFor="orientationGuidance" className="text-sm font-medium cursor-pointer">
                Orientation Guidance
              </label>
              <p className="text-xs text-gray-600 mt-1">
                Shows a GPS direction arrow and distance on each stop screen (outdoors only), and adds a "Places" tab to search the guide's stops. Each stop can override this under Location → "Direction arrow on this stop". Saved as `metadata.orientation`.
              </p>
            </div>
          </div>

          <div className={isTranslatedSharedFieldLocked('map_display') ? 'pointer-events-none opacity-60' : ''}>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-slate-700">Map Stop Visibility</h4>
                <p className="mt-1 text-xs text-gray-600">
                  Saved in `metadata.map_display` so the frontend can decide which stop types appear on the map and whether each icon shows its label.
                </p>
              </div>

              <div className="space-y-3">
                {[
                  { key: 'popup_stop', title: 'Popup stop', description: 'Usually used for smaller contextual markers.' },
                  { key: 'stop', title: 'Stop', description: 'Standard narrated stop on the guide.' }
                ].map(({ key, title, description }) => (
                  <div key={key} className="rounded-md border border-slate-200 bg-white p-3">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="text-sm font-medium text-slate-800">{title}</div>
                        <p className="mt-1 text-xs text-gray-600">{description}</p>
                      </div>

                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={Boolean(mapDisplaySettings[key].visibility)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setItinerary(prev => ({
                                ...prev,
                                metadata: {
                                  ...prev.metadata,
                                  map_display: {
                                    ...normalizeMapDisplaySettings(prev.metadata.map_display),
                                    [key]: {
                                      ...normalizeMapDisplaySettings(prev.metadata.map_display)[key],
                                      visibility: checked
                                    }
                                  }
                                }
                              }));
                            }}
                            disabled={isTranslatedSharedFieldLocked('map_display')}
                          />
                          <span>Visible on map</span>
                        </label>

                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={Boolean(mapDisplaySettings[key].label)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setItinerary(prev => ({
                                ...prev,
                                metadata: {
                                  ...prev.metadata,
                                  map_display: {
                                    ...normalizeMapDisplaySettings(prev.metadata.map_display),
                                    [key]: {
                                      ...normalizeMapDisplaySettings(prev.metadata.map_display)[key],
                                      label: checked
                                    }
                                  }
                                }
                              }));
                            }}
                            disabled={isTranslatedSharedFieldLocked('map_display')}
                          />
                          <span>Show label</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Download on Location */}
          <div>
            <div className="flex items-start">
              <div className="flex-1">
                <label htmlFor="downloadOnLocation" className="text-sm font-medium cursor-pointer">
                  Download on Location
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  Choose how the guide should behave when users reach the specified location.
                </p>
              </div>
            </div>

            <select
              id="downloadOnLocation"
              className="mt-3 w-full p-2 border rounded"
              value={downloadOnLocation}
              onChange={(e) => setItinerary(prev => {
                const metadata = {
                  ...prev.metadata,
                };

                if (e.target.value === 'off') {
                  delete metadata.download_on_location;
                  delete metadata.download_radius_meters;
                  delete metadata.download_priority;
                } else {
                  metadata.download_on_location = e.target.value;
                }

                delete metadata.force_download_on_location;

                return { ...prev, metadata };
              })}
              disabled={isTranslatedSharedFieldLocked('download_on_location')}
            >
              <option value="off">Off</option>
              <option value="ask">Ask</option>
              <option value="force">Force</option>
            </select>

            {/* Radius and priority matter when the location trigger is active */}
            {downloadOnLocation !== 'off' && (
              <div className="ml-7 mt-3 space-y-4 p-3 bg-gray-100 border border-gray-200 rounded">
                {/* Download Radius */}
                <div>
                  <label className="block text-sm font-medium mb-1">Download Radius (meters)</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded"
                    placeholder="5000"
                    value={itinerary.metadata.download_radius_meters || ''}
                    onChange={(e) => setItinerary(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, download_radius_meters: parseInt(e.target.value) || 5000 }
                    }))}
                    disabled={isTranslatedSharedFieldLocked('download_radius_meters')}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Radius around the location coordinates where download will be triggered (default: 5000m = 5km)
                  </p>
                </div>

                {/* Download Priority */}
                <div>
                  <label className="block text-sm font-medium mb-1">Download Priority</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={itinerary.metadata.download_priority || 'normal'}
                    onChange={(e) => setItinerary(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, download_priority: e.target.value }
                    }))}
                    disabled={isTranslatedSharedFieldLocked('download_priority')}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Priority level for the download when triggered
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* B2B Client Guide */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="b2bClient"
              checked={Boolean(b2bClient)}
              onChange={(e) => {
                if (isTranslatedSharedFieldLocked('b2bClient')) return;
                const checked = e.target.checked;
                setB2bClient(checked);
                setItinerary(prev => ({
                  ...prev,
                  metadata: {
                    ...prev.metadata,
                    b2bClient: checked
                  }
                }));
              }}
              className="w-4 h-4 mr-3 mt-0.5"
              disabled={isTranslatedSharedFieldLocked('b2bClient')}
            />
            <div className="flex-1">
              <label htmlFor="b2bClient" className="text-sm font-medium cursor-pointer">
                B2B Client Guide (Paid)
              </label>
              <p className="text-xs text-gray-600 mt-1">
                Mark this guide as a paid B2B client guide. This will add a "b2b_client" tag to the database.
              </p>
            </div>
          </div>

          {itinerary.metadata.guide_type === 'bus_tour' && (
            <div>
              <p className="text-xs text-gray-600 mt-1">
                Bus Tour enables fixed-radius stop auto-play behavior in the app.
              </p>
              <div className="ml-7 mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
                <label className="block text-sm font-medium mb-1">
                  Fixed Auto-play Radius (meters)
                </label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  placeholder="50"
                  min="5"
                  step="5"
                  value={itinerary.metadata.auto_play_radius_meters || ''}
                  onChange={(e) => setItinerary(prev => ({
                    ...prev,
                    metadata: {
                      ...prev.metadata,
                      auto_play_radius_meters: parseInt(e.target.value, 10) || 50
                    }
                  }))}
                  disabled={isTranslatedSharedFieldLocked('auto_play_radius_meters')}
                />
                <p className="text-xs text-gray-500 mt-1">
                  The app uses this fixed radius for bus-tour stop activation and hides the manual radius slider.
                </p>
              </div>
            </div>
          )}

          {/* AI Radius Limitation */}
          <div>
            <div className="flex items-start">
              <input
                type="checkbox"
                id="aiRadiusLimitation"
                checked={Boolean(itinerary.metadata.ai_radius_limitation)}
                onChange={(e) => setItinerary(prev => ({
                  ...prev,
                  metadata: { ...prev.metadata, ai_radius_limitation: e.target.checked }
                }))}
                className="w-4 h-4 mr-3 mt-0.5"
                disabled={isTranslatedSharedFieldLocked('ai_radius_limitation')}
              />
              <div className="flex-1">
                <label htmlFor="aiRadiusLimitation" className="text-sm font-medium cursor-pointer">
                  AI Generation Radius Limitation
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  Require visitors to be near this guide before creating new content in any AI tab. Official recordings and previously cached AI stories remain playable.
                </p>
              </div>
            </div>

            {/* AI Radius Value - Show when AI radius limitation is enabled */}
            {itinerary.metadata.ai_radius_limitation === true && (
              <div className="ml-7 mt-3 p-3 bg-blue-100 border border-blue-200 rounded">
                <label className="block text-sm font-medium mb-1">
                  AI Access Radius (meters)
                </label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  placeholder="1000"
                  min="100"
                  step="100"
                  value={itinerary.metadata.ai_radius_meters || ''}
                  onChange={(e) => setItinerary(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, ai_radius_meters: parseInt(e.target.value) || 1000 }
                  }))}
                  disabled={isTranslatedSharedFieldLocked('ai_radius_meters')}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Users must be within this radius to generate AI content. Default: 1000m. Common values: 500m for a museum, 1000m for a compact venue, or 3000m for a town guide.
                </p>
              </div>
            )}
          </div>

          {/* 360 Visit */}
          <div>
            <div className="flex items-start">
              <input
                type="checkbox"
                id="360visit"
                checked={Boolean(itinerary.metadata['360visit'])}
                onChange={(e) => setItinerary(prev => ({
                  ...prev,
                  metadata: { ...prev.metadata, '360visit': e.target.checked }
                }))}
                className="w-4 h-4 mr-3 mt-0.5"
                disabled={isTranslatedSharedFieldLocked('360visit')}
              />
              <div className="flex-1">
                <label htmlFor="360visit" className="text-sm font-medium cursor-pointer">
                  360° Visit Available
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  Enable if this guide includes 360° virtual tour content.
                </p>
              </div>
            </div>

            {/* 360 Visit fields - Show when 360visit is enabled */}
            {itinerary.metadata['360visit'] === true && (
              <div className="ml-7 mt-3 p-3 bg-gray-100 border border-gray-200 rounded space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    360° Visit JSON URL
                  </label>
                  <input
                    type="url"
                    className="w-full p-2 border rounded"
                    placeholder="https://.../.json"
                    value={itinerary.metadata['360visitJSON'] || ''}
                    onChange={(e) => setItinerary(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, '360visitJSON': e.target.value }
                    }))}
                    disabled={isTranslatedSharedFieldLocked('360visitJSON')}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    URL to the 360° tour JSON configuration file
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    360° Visit URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    className={`w-full p-2 border rounded ${
                      !itinerary.metadata['360visitUrl'] ? 'border-yellow-500' : ''
                    }`}
                    placeholder="https://guidesofiavirtual360.web.app/tour/?id=tour_..."
                    value={itinerary.metadata['360visitUrl'] || ''}
                    onChange={(e) => setItinerary(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, '360visitUrl': e.target.value }
                    }))}
                    disabled={isTranslatedSharedFieldLocked('360visitUrl')}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Required: Enter the full URL for the 360° virtual tour
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Web Based Guide */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="webBasedGuide"
              checked={Boolean(itinerary.metadata.web_based_guide)}
              onChange={(e) => setItinerary(prev => ({
                ...prev,
                metadata: { ...prev.metadata, web_based_guide: e.target.checked }
              }))}
              className="w-4 h-4 mr-3 mt-0.5"
              disabled={isTranslatedSharedFieldLocked('web_based_guide')}
            />
            <div className="flex-1">
              <label htmlFor="webBasedGuide" className="text-sm font-medium cursor-pointer">
                Web-Based Guide
              </label>
              <p className="text-xs text-gray-600 mt-1">
                Mark this guide as optimized and available for web-based access. Users can view this guide at museum1guide.guidesofia.com
              </p>
            </div>
          </div>
          {/* Collect Feedback */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="feedbackCollect"
              checked={Boolean(itinerary.metadata.feedbackCollect)}
              onChange={(e) => {
                if (isTranslatedSharedFieldLocked('feedbackCollect')) return;
                const checked = e.target.checked;
                setItinerary(prev => ({
                  ...prev,
                  metadata: {
                    ...prev.metadata,
                    feedbackCollect: checked,
                    feedback: checked
                      ? normalizeFeedbackConfig(prev.metadata.feedback, prev.metadata.language)
                      : prev.metadata.feedback,
                  }
                }));
              }}
              className="w-4 h-4 mr-3 mt-0.5"
              disabled={isTranslatedSharedFieldLocked('feedbackCollect')}
            />
            <div className="flex-1">
              <label htmlFor="feedbackCollect" className="text-sm font-medium cursor-pointer">
                Collect Feedback
              </label>
              <p className="text-xs text-gray-600 mt-1">
                Enable feedback collection for this guide and save it in the guide JSON metadata.
              </p>
            </div>
          </div>

          <div className={`ml-7 mt-3 space-y-3 ${itinerary.metadata.feedbackCollect ? '' : 'hidden'}`}>
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs text-slate-500">
                  Questions are saved in `metadata.feedback` and stay collapsed until you edit them.
                </p>
                <button
                  type="button"
                  onClick={() => setShowFeedbackEditor((current) => !current)}
                  className="px-3 py-2 bg-slate-100 text-slate-700 rounded text-xs hover:bg-slate-200"
                >
                  {showFeedbackEditor ? 'Hide questions' : 'Configure questions'}
                </button>
              </div>

              <div className={`p-4 border border-slate-200 rounded bg-white space-y-4 ${showFeedbackEditor ? '' : 'hidden'}`}>
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Feedback Questions</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Configure up to 3 micro prompts. The next prompt appears only after a positive answer.
                    </p>
                  </div>
                  <div className="flex items-end gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Template language</label>
                      <select
                        className="p-2 border rounded text-sm bg-white"
                        value={feedbackTemplateLanguage}
                        onChange={(e) => {
                          const language = e.target.value;
                          updateFeedbackMetadata(() => createDefaultFeedbackConfig(language));
                        }}
                      >
                        {FEEDBACK_TEMPLATE_LANGUAGES.map((language) => (
                          <option key={language} value={language}>
                            {language.toUpperCase()}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-500 mt-1">
                        Switching loads that language&apos;s generic defaults.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={resetFeedbackToDefaults}
                      className="px-3 py-2 bg-slate-100 text-slate-700 rounded text-xs hover:bg-slate-200"
                    >
                      Use generic set
                    </button>
                  </div>
                </div>

              <div className="space-y-4">
                {(normalizeFeedbackConfig(itinerary.metadata.feedback, itinerary.metadata.language).micro_prompts || []).map((question, questionIndex) => (
                  <div key={question.id || questionIndex} className="p-3 border border-slate-200 rounded bg-slate-50 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-medium text-slate-700">
                        Prompt {questionIndex + 1}
                        {isPositiveQuestionKind(question.kind) ? '' : ' (custom)'}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeMicroPrompt(questionIndex)}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Question ID</label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded text-sm"
                          value={question.id || ''}
                          onChange={(e) => updateMicroPrompt(questionIndex, (current) => ({ ...current, id: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Question Type</label>
                        <select
                          className="w-full p-2 border rounded text-sm"
                          value={question.kind || 'visit_impact'}
                          onChange={(e) => updateMicroPrompt(questionIndex, (current) => ({ ...current, kind: e.target.value }))}
                        >
                          {questionKindOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1">Title</label>
                      <input
                        type="text"
                        className="w-full p-2 border rounded text-sm"
                        value={question.title || ''}
                        onChange={(e) => updateMicroPrompt(questionIndex, (current) => ({ ...current, title: e.target.value }))}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1">Prompt</label>
                      <textarea
                        className="w-full p-2 border rounded text-sm"
                        rows={2}
                        value={question.prompt || ''}
                        onChange={(e) => updateMicroPrompt(questionIndex, (current) => ({ ...current, prompt: e.target.value }))}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1">Feedback Type</label>
                      <input
                        type="text"
                        className="w-full p-2 border rounded text-sm"
                        value={question.feedbackType || 'micro_prompt'}
                        onChange={(e) => updateMicroPrompt(questionIndex, (current) => ({ ...current, feedbackType: e.target.value }))}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="block text-xs font-medium">Options</label>
                        <button
                          type="button"
                          onClick={() => addQuestionOption(questionIndex)}
                          className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                        >
                          Add option
                        </button>
                      </div>
                      {(question.options || []).map((option, optionIndex) => (
                        <div key={option.id || optionIndex} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                          <input
                            type="text"
                            className="md:col-span-3 p-2 border rounded text-sm"
                            placeholder="Label"
                            value={option.label || ''}
                            onChange={(e) => updateQuestionOption(questionIndex, optionIndex, (current) => ({ ...current, label: e.target.value }))}
                          />
                          <input
                            type="text"
                            className="md:col-span-3 p-2 border rounded text-sm"
                            placeholder="Value"
                            value={option.value || ''}
                            onChange={(e) => updateQuestionOption(questionIndex, optionIndex, (current) => ({ ...current, value: e.target.value }))}
                          />
                          <input
                            type="number"
                            className="md:col-span-2 p-2 border rounded text-sm"
                            placeholder="Rating"
                            value={option.rating === '' ? '' : option.rating}
                            onChange={(e) => updateQuestionOption(questionIndex, optionIndex, (current) => ({ ...current, rating: e.target.value === '' ? '' : Number(e.target.value) }))}
                          />
                          <label className="md:col-span-2 flex items-center gap-2 text-xs text-slate-600">
                            <input
                              type="checkbox"
                              checked={Boolean(option.advances_to_next)}
                              onChange={(e) => updateQuestionOption(questionIndex, optionIndex, (current) => ({ ...current, advances_to_next: e.target.checked }))}
                            />
                            Positive
                          </label>
                          <button
                            type="button"
                            onClick={() => removeQuestionOption(questionIndex, optionIndex)}
                            className="md:col-span-2 text-xs text-red-600 hover:text-red-700 justify-self-start"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  The app reads this block from `metadata.feedback` directly.
                </p>
                <button
                  type="button"
                  onClick={addMicroPrompt}
                  disabled={normalizeFeedbackConfig(itinerary.metadata.feedback, itinerary.metadata.language).micro_prompts.length >= 3}
                  className="px-3 py-2 bg-indigo-500 text-white rounded text-xs hover:bg-indigo-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Add micro prompt
                </button>
              </div>

              <div className="pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Issue Recovery Question</p>
                    <p className="text-xs text-slate-500">Shown only if the user answers negatively.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => updateIssueQuestion(() => cloneFeedbackValue(createDefaultFeedbackConfig(itinerary.metadata.language).issue_question))}
                    className="px-3 py-2 bg-slate-100 text-slate-700 rounded text-xs hover:bg-slate-200"
                  >
                    Restore default
                  </button>
                </div>

                {(() => {
                  const issueQuestion = normalizeFeedbackConfig(itinerary.metadata.feedback, itinerary.metadata.language).issue_question;
                  return (
                    <div className="p-3 border border-slate-200 rounded bg-slate-50 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">Question ID</label>
                          <input
                            type="text"
                            className="w-full p-2 border rounded text-sm"
                            value={issueQuestion.id || ''}
                            onChange={(e) => updateIssueQuestion((current) => ({ ...current, id: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Question Type</label>
                          <select
                            className="w-full p-2 border rounded text-sm"
                            value={issueQuestion.kind || 'issue_picker'}
                            onChange={(e) => updateIssueQuestion((current) => ({ ...current, kind: e.target.value }))}
                          >
                            <option value="issue_picker">Issue picker</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Title</label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded text-sm"
                          value={issueQuestion.title || ''}
                          onChange={(e) => updateIssueQuestion((current) => ({ ...current, title: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Prompt</label>
                        <textarea
                          className="w-full p-2 border rounded text-sm"
                          rows={2}
                          value={issueQuestion.prompt || ''}
                          onChange={(e) => updateIssueQuestion((current) => ({ ...current, prompt: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Feedback Type</label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded text-sm"
                          value={issueQuestion.feedbackType || 'issue_recovery'}
                          onChange={(e) => updateIssueQuestion((current) => ({ ...current, feedbackType: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="block text-xs font-medium">Issue options</label>
                          <button
                            type="button"
                            onClick={() => updateIssueQuestion((current) => ({
                              ...current,
                              options: [
                                ...(current.options || []),
                                {
                                  id: `issue_${(current.options || []).length + 1}`,
                                  label: `Issue ${(current.options || []).length + 1}`,
                                  value: `issue_${(current.options || []).length + 1}`,
                                },
                              ],
                            }))}
                            className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                          >
                            Add option
                          </button>
                        </div>
                        {(issueQuestion.options || []).map((option, optionIndex) => (
                          <div key={option.id || optionIndex} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                            <input
                              type="text"
                              className="md:col-span-4 p-2 border rounded text-sm"
                              placeholder="Label"
                              value={option.label || ''}
                              onChange={(e) => updateIssueQuestion((current) => ({
                                ...current,
                                options: current.options.map((item, currentIndex) =>
                                  currentIndex === optionIndex ? { ...item, label: e.target.value } : item
                                ),
                              }))}
                            />
                            <input
                              type="text"
                              className="md:col-span-4 p-2 border rounded text-sm"
                              placeholder="Value"
                              value={option.value || ''}
                              onChange={(e) => updateIssueQuestion((current) => ({
                                ...current,
                                options: current.options.map((item, currentIndex) =>
                                  currentIndex === optionIndex ? { ...item, value: e.target.value } : item
                                ),
                              }))}
                            />
                            <button
                              type="button"
                              onClick={() => updateIssueQuestion((current) => ({
                                ...current,
                                options: current.options.filter((_, currentIndex) => currentIndex !== optionIndex),
                              }))}
                              className="md:col-span-4 text-xs text-red-600 hover:text-red-700 justify-self-start"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>



        </div>
        </div>
      </CollapsibleSection>

      {/* --- Group: Translation Settings --- */}
      {getFieldAccess('translated_itinerary') !== 'hidden' && (
        <CollapsibleSection id="translation-settings" title="Translation Settings" summary="Mark as translated guide, parent guide link" className="mt-6">

          {/* Translated Itinerary */}
          <div className="mb-4">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="translatedItinerary"
                checked={Boolean(itinerary.metadata.translated_itinerary === 'yes')}
                onChange={(e) => setItinerary(prev => ({
                  ...prev,
                  metadata: {
                    ...prev.metadata,
                    translated_itinerary: e.target.checked ? 'yes' : 'no',
                    parent_guide_id: e.target.checked
                      ? (prev.metadata.parent_guide_id || '')
                      : prev.metadata.id
                  }
                }))}
                className="w-4 h-4 mr-3 mt-0.5"
                disabled={!hasFieldAccess('translated_itinerary', 'edit')}
              />
              <div className="flex-1">
                <label htmlFor="translatedItinerary" className="text-sm font-medium cursor-pointer">
                  Translated Itinerary
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  Check if this itinerary is a translation of another itinerary.
                </p>
              </div>
            </div>
            {itinerary.metadata.translated_itinerary === 'yes' && getFieldAccess('parent_guide_id') !== 'hidden' && (
              <div className="mt-2 ml-7">
                <label className="block text-sm font-medium mb-1">Parent Guide ID</label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded ${errors.translation_parent ? 'border-red-500' : ''}`}
                  placeholder="Enter parent guide ID"
                  value={itinerary.metadata.parent_guide_id || ''}
                  onChange={(e) => setItinerary(prev => ({
                    ...prev,
                    metadata: { ...prev.metadata, parent_guide_id: e.target.value }
                  }))}
                  disabled={!hasFieldAccess('parent_guide_id', 'edit')}
                />
                <p className="text-xs text-gray-600 mt-1">
                  Use the root/original guide ID for all translations in the same family.
                </p>
                {errors.translation_parent && (
                  <p className="text-xs text-red-600 mt-1">{errors.translation_parent}</p>
                )}
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* --- Group: Archetype Classification --- */}
      <div id="archetype-classification" className="mt-6">
        <div className={isTranslatedSharedFieldLocked('travel_dna_archetype') ? 'pointer-events-none opacity-60' : ''}>
          <ArchetypeClassifier itinerary={itinerary} setItinerary={setItinerary} />
        </div>
        {isTranslatedSharedFieldLocked('travel_dna_archetype') && (
          <p className="mt-2 text-xs text-slate-500">
            Travel DNA archetype is managed by the parent guide for translated versions.
          </p>
        )}
      </div>

      {/* --- Group: Actions --- */}
      <div id="actions" className="mt-6">
        <div className="mb-4">
          <button
            onClick={() => {
              if (validateBeforeSave()) {
                setStatus('success');
                setMessage('All required fields are filled correctly!');
              }
            }}
            className="px-3 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 text-sm"
          >
            Validate Required Fields
          </button>
        </div>
      </div>
    </div>
  );
};
